// Adapted from https://stackoverflow.com/a/75920691
// Posted by PhilippeP, modified by community. See post 'Timeline' for change history
// Retrieved 2026-09-08, License - CC BY-SA 4.0

import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../model/db.js';

export enum Status {
  PENDING = 'pending',
  RESOLVED = 'resolved',
}

export type AsyncLiveQueryReturn<T = unknown> = AsyncLiveQueryReturnPending | AsyncLiveQueryReturnResolved<T>;

export interface AsyncLiveQueryReturnPending {
  isLoading: true;
  isSuccess: false;
  status: Status.PENDING;
  data: null;
}

export interface AsyncLiveQueryReturnResolved<T = unknown> {
  isLoading: false;
  isSuccess: true;
  status: Status.RESOLVED;
  data: T extends Array<infer U> ? Array<U> : T | undefined;
}

export interface AsyncLiveQueryReturnWithDefault<T = unknown> {
  isLoading: boolean;
  isSuccess: boolean;
  status: Status;
  data: T;
}

// overload used when a default is supplied: since a resolved value is guaranteed
// (missing/pending states both fall back to defaultIfMissing), `data` is just `T`
export function useAsyncLiveQuery<T>(
  querier: (dbInstance: typeof db) => Promise<T>,
  deps: unknown[],
  defaultIfMissing: T
): AsyncLiveQueryReturnWithDefault<T>;
export function useAsyncLiveQuery<T>(
  querier: (dbInstance: typeof db) => Promise<T>,
  deps?: unknown[],
  defaultIfMissing?: undefined
): AsyncLiveQueryReturn<T>;
export function useAsyncLiveQuery<T>(
  querier: (dbInstance: typeof db) => Promise<T>,
  deps: unknown[] = [],
  defaultIfMissing?: T
): AsyncLiveQueryReturn<T> | AsyncLiveQueryReturnWithDefault<T> {
  const hasDefault = defaultIfMissing !== undefined;

  const [data, status] = useLiveQuery(
    async () => {
      return querier(db).then((result: T) => {
        const resolved = result === undefined ? defaultIfMissing : result;
        return [resolved, Status.RESOLVED] as const;
      });
    },
    deps,
    [hasDefault ? defaultIfMissing : null, Status.PENDING] as const
  );

  return {
    isLoading: status === Status.PENDING,
    isSuccess: status === Status.RESOLVED,
    status,
    data,
  } as AsyncLiveQueryReturn<T> | AsyncLiveQueryReturnWithDefault<T>;
}
